import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/modules", label: "Modules" },
  { to: "/pricing", label: "Pricing" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? "ab-glass" : "bg-transparent"}`}
    >
      <div className="ab-container flex items-center justify-between h-16 md:h-20">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 group">
          <span className="relative flex items-center justify-center w-9 h-9 rounded-full bg-ab-primary text-white">
            <span className="absolute inset-0 rounded-full ring-1 ring-ab-primary/30 group-hover:ring-ab-accent/40 transition-all" />
            <span className="font-serif text-lg leading-none">A</span>
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-serif text-2xl tracking-tight text-ab-primary">PranaOs</span>
            <span className="ab-eyebrow text-[9px] text-ab-muted">Hospital · OS</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1 p-1 rounded-full border border-ab-border bg-white/60 backdrop-blur">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-link-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `px-5 py-2 rounded-full text-sm transition-all ${isActive ? "bg-ab-primary text-white" : "text-ab-text hover:bg-ab-bg"}`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href="#contact"
            data-testid="nav-book-demo-btn"
            className="ab-pill-btn group text-sm"
            onClick={(e) => {
              if (pathname === "/") {
                e.preventDefault();
                document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
              }
            }}
          >
            Book a Demo
            <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </div>

        <button
          data-testid="nav-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden p-2 rounded-full border border-ab-border bg-white"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="md:hidden ab-glass border-t border-ab-border"
          >
            <div className="ab-container py-6 flex flex-col gap-2">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === "/"}
                  data-testid={`nav-mobile-${n.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `px-4 py-3 rounded-xl text-base ${isActive ? "bg-ab-primary text-white" : "bg-white border border-ab-border"}`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
              <Link to="/" onClick={() => setTimeout(() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" }), 200)} className="ab-pill-btn justify-center mt-2" data-testid="nav-mobile-cta">
                Book a Demo <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
